import React, { useState, useEffect, useRef } from 'react'
import { Routes, Route, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { useBranding } from '../../context/BrandingContext'
import NewRequestModal from './NewRequestModal'
import '../../styles/client-pages.css'

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcSend = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
)
const IcPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
)
const IcSearch = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
)
const IcCheck = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const IcUpload = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
)
const IcFile = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
)
const IcInbox = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
  </svg>
)
const IcBolt = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
)
const IcActivity = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
)
const IcMsg = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
)

// ── Status config (mirrors the admin board grouping) ────────────────────────────
const STATUS = {
  pending:       { tone: 'warning', label: 'Open' },
  open:          { tone: 'warning', label: 'Open' },
  approved:      { tone: 'info',    label: 'In progress' },
  'in-progress': { tone: 'info',    label: 'In progress' },
  in_progress:   { tone: 'info',    label: 'In progress' },
  'in-review':   { tone: 'accent',  label: 'In review' },
  completed:     { tone: 'success', label: 'Done' },
  rejected:      { tone: 'danger',  label: 'Rejected' },
}

// Timeline follows the admin board order: Submitted → In progress → In review → Done.
const STAGE_ORDER = ['Submitted', 'In progress', 'In review', 'Done']
const STAGE_INDEX = { open: 0, pending: 0, approved: 1, 'in-progress': 1, in_progress: 1, 'in-review': 2, completed: 3 }

function getSteps(status) {
  const cur = STAGE_INDEX[status] ?? 0
  const isDone = status === 'completed'
  return STAGE_ORDER.map((label, i) => ({
    label,
    done: isDone ? true : i < cur,
    active: isDone ? false : i === cur,
  }))
}

// ── Helpers ─────────────────────────────────────────────────────────────────────
const relTime = (iso) => {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}
const fmtSize = (b) => {
  if (b == null) return ''
  if (b < 1024) return `${b} B`
  if (b < 1048576) return `${Math.round(b / 1024)} KB`
  return `${(b / 1048576).toFixed(1)} MB`
}

// ── Request list item ─────────────────────────────────────────────────────────
function ClRequestListItem({ r, active, onClick, cmsName }) {
  const b = STATUS[r.status] || STATUS.pending
  // Preview the most recent conversation message (from either side); fall back
  // to the original request description.
  const lastBody = (r.last_message_body || r.description || '').replace(/\s+/g, ' ').trim()
  const fromTeam = !!r.last_message_body && r.last_message_role && r.last_message_role !== 'client'
  const prefix = lastBody ? (fromTeam ? `${cmsName} team: ` : 'You: ') : ''
  return (
    <button
      onClick={onClick}
      style={{
        textAlign: 'left', padding: 13, borderRadius: 12, cursor: 'pointer',
        border: `1px solid ${active ? 'var(--border-accent)' : 'var(--border)'}`,
        background: active ? 'var(--accent-soft)' : 'var(--surface)',
        transition: 'all 0.14s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
        <span className={`badge badge-${b.tone}`} style={{ fontSize: 10 }}><span className="badge-dot" /> {b.label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{relTime(r.updated_at || r.created_at)}</span>
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text)', lineHeight: 1.35, marginBottom: 6 }}>{r.title}</div>
      {lastBody && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prefix}{lastBody}</div>}
    </button>
  )
}

// ── Conversation panel ──────────────────────────────────────────────────────────
function ThreadPanel({ request }) {
  const { cmsName } = useBranding()
  const queryClient = useQueryClient()
  const [reply, setReply] = useState('')
  const [files, setFiles] = useState([])
  const fileInputRef = useRef(null)

  const { data: messages = [] } = useQuery(
    ['request-messages', request?.id],
    () => apiService.get(`/requests/${request.id}/messages`).then(r => r.data),
    { enabled: !!request?.id, refetchOnWindowFocus: false }
  )

  const replyMutation = useMutation(
    ({ body, attachFiles }) => {
      const fd = new FormData()
      if (body) fd.append('body', body)
      ;(attachFiles || []).forEach(f => fd.append('files', f))
      return apiService.post(`/requests/${request.id}/messages`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    {
      onSuccess: () => {
        toast.success('Reply sent!')
        queryClient.invalidateQueries(['request-messages', request.id])
        queryClient.invalidateQueries(['edit-requests'])
        setReply('')
        setFiles([])
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to send reply')
    }
  )

  if (!request) {
    return (
      <div className="card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className="thread-empty" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--text-faint)', padding: 40 }}>
          <IcMsg />
          <div style={{ fontSize: 14, fontWeight: 500 }}>No request selected</div>
          <div style={{ fontSize: 12 }}>Select a request from the list to view the conversation</div>
        </div>
      </div>
    )
  }

  const b = STATUS[request.status] || STATUS.pending
  const steps = getSteps(request.status)

  // Unified conversation: original description, the message thread, then any
  // notes from approve/complete/reject — sorted chronologically.
  const thread = []
  if (request.description) {
    thread.push({ id: '__desc', role: 'client', name: request.client_name || 'You', body: request.description, at: request.created_at, attachments: [] })
  }
  messages.forEach(m => thread.push({ id: m.id, role: m.author_role, name: m.author_name, body: m.body, at: m.created_at, attachments: m.attachments || [] }))
  if (request.approval_notes)   thread.push({ id: '__appr', role: 'admin', body: request.approval_notes, at: request.approved_at, attachments: [] })
  if (request.completion_notes) thread.push({ id: '__comp', role: 'admin', body: request.completion_notes, at: request.completed_at, attachments: [] })
  if (request.rejection_reason) thread.push({ id: '__rej',  role: 'admin', body: request.rejection_reason, at: request.updated_at, attachments: [], tone: 'danger' })
  thread.sort((a, z) => (a.at ? new Date(a.at).getTime() : Infinity) - (z.at ? new Date(z.at).getTime() : Infinity))

  const send = () => {
    if (!reply.trim() && files.length === 0) { toast.error('Add a message or attachment'); return }
    replyMutation.mutate({ body: reply.trim(), attachFiles: files })
  }

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Head */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className={`badge badge-${b.tone}`}><span className="badge-dot" /> {b.label}</span>
            {request.category && <span className="badge" style={{ fontSize: 10 }}>{request.category}</span>}
          </div>
          <h2 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', marginTop: 8, color: 'var(--text)' }}>{request.title}</h2>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 3 }}>
            Opened {request.created_at ? new Date(request.created_at).toLocaleString() : ''}{request.site_name ? ` · ${request.site_name}` : ''}
          </div>
        </div>
        <div className="avatar-stack">
          <div className="avatar avatar-grad-1">{(cmsName || 'T').slice(0, 1).toUpperCase()}</div>
          <div className="avatar avatar-grad-4">{(request.client_name || 'You').slice(0, 1).toUpperCase()}</div>
        </div>
      </div>

      {/* Progress steps */}
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <div style={{ display: 'flex', gap: 0 }}>
          {steps.map((st, i) => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, position: 'relative' }}>
              {i < steps.length - 1 && (
                <div style={{ position: 'absolute', top: 8, left: '50%', right: '-50%', height: 2, background: st.done ? 'var(--success)' : 'var(--border)' }} />
              )}
              <span className={`vtl-dot ${st.done ? 'done' : st.active ? 'active' : ''}`} style={{ width: 18, height: 18, zIndex: 1 }}>{st.done ? <IcCheck /> : ''}</span>
              <span style={{ fontSize: 10.5, color: (st.done || st.active) ? 'var(--text-soft)' : 'var(--text-faint)', textAlign: 'center', lineHeight: 1.3, maxWidth: 110 }}>{st.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div className="thread">
          {thread.map(m => {
            const mine = m.role === 'client'
            return (
              <div key={m.id} className={`msg ${mine ? 'you' : ''}`}>
                <div className={`avatar avatar-grad-${mine ? 4 : 1}`} style={{ width: 28, height: 28, fontSize: 10, flexShrink: 0 }}>
                  {mine ? (m.name || 'Y').slice(0, 1).toUpperCase() : (cmsName || 'T').slice(0, 1).toUpperCase()}
                </div>
                <div className="msg-body">
                  <div className="msg-meta">
                    <strong style={{ color: 'var(--text-soft)' }}>{mine ? 'You' : (m.name ? `${m.name} · ${cmsName}` : `${cmsName} team`)}</strong>
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{m.at ? new Date(m.at).toLocaleString() : ''}</span>
                  </div>
                  {m.body && <div className="msg-bubble" style={m.tone === 'danger' ? { borderColor: 'var(--danger)' } : undefined}>{m.body}</div>}
                  {m.attachments.map((a, j) => (
                    <div key={j} className="attach-chip">
                      <span className="a-icon"><IcFile /></span>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ color: 'var(--text)', fontWeight: 500 }}>{a.file_name}</span>
                        <span style={{ fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{fmtSize(a.file_size)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Composer */}
      <div style={{ padding: 14, borderTop: '1px solid var(--border)', background: 'var(--surface)' }}>
        {files.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {files.map((f, i) => (
              <span key={i} className="attach-chip">
                <span className="a-icon"><IcFile /></span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: 'var(--text)', fontWeight: 500 }}>{f.name}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{fmtSize(f.size)}</span>
                </div>
                <button onClick={() => setFiles(fs => fs.filter((_, x) => x !== i))} style={{ marginLeft: 4, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
        )}
        <div className="reply-box" style={{ background: 'var(--bg-1)' }}>
          <textarea placeholder="Type your reply…" value={reply} onChange={e => setReply(e.target.value)} />
          <div className="reply-tools">
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => { setFiles(fs => [...fs, ...Array.from(e.target.files)]); e.target.value = '' }} />
            <button className="btn btn-sm btn-ghost" onClick={() => fileInputRef.current?.click()}><IcUpload /> Attach</button>
            <button className="btn btn-sm btn-primary" style={{ marginLeft: 'auto' }} disabled={(!reply.trim() && files.length === 0) || replyMutation.isLoading} onClick={send}>
              <IcSend /> {replyMutation.isLoading ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Soft stat chip ──────────────────────────────────────────────────────────────
function ClSoftStat({ label, value, sub, tone, icon }) {
  return (
    <div className="stat-chip">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className={`icon-tile ${tone || ''}`} style={{ width: 26, height: 26, fontSize: 12 }}>{icon}</span>
        <div className="lbl" style={{ marginTop: 0 }}>{label}</div>
      </div>
      <div className="num" style={{ marginTop: 8 }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

// ── List view ─────────────────────────────────────────────────────────────────
const ClientRequestsList = ({ openNew = false }) => {
  const navigate = useNavigate()
  const { cmsName } = useBranding()
  const [search, setSearch]         = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [showNew, setShowNew]       = useState(openNew)
  const [searchParams]              = useSearchParams()

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) setSelectedId(openId)
  }, [searchParams])

  const { data: requests = [], isLoading } = useQuery(
    ['edit-requests'],
    () => apiService.get('/requests').then(res => res.data)
  )

  const filtered = requests.filter(r =>
    r.title?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase())
  )
  const selected = filtered.find(r => String(r.id) === String(selectedId)) || filtered[0] || null

  const isOpen = (s) => s === 'pending' || s === 'open'
  const isProg = (s) => ['approved', 'in-progress', 'in_progress', 'in-review'].includes(s)
  const counts = {
    open: requests.filter(r => isOpen(r.status)).length,
    prog: requests.filter(r => isProg(r.status)).length,
    done: requests.filter(r => r.status === 'completed').length,
  }

  const closeModal = () => { setShowNew(false); if (openNew) navigate('/client/requests') }

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1>Requests</h1>
          <div className="subtitle">Ask your team to make any change — typical first reply in <strong style={{ color: 'var(--text)' }}>under 4 hours</strong>.</div>
        </div>
        <div className="flex gap-2 items-center">
          <Link to="/client/help"><button className="btn">Help</button></Link>
          <button className="btn btn-primary" onClick={() => setShowNew(true)}><IcPlus /> New request</button>
        </div>
      </div>

      {showNew && <NewRequestModal onClose={closeModal} />}

      <div className="page-body">
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <ClSoftStat label="Open"            value={counts.open} sub="awaiting your team" tone="warning" icon={<IcInbox />} />
          <ClSoftStat label="In progress"     value={counts.prog} sub="being worked on"    tone="info"    icon={<IcBolt />} />
          <ClSoftStat label="Completed"       value={counts.done} sub="wrapped up"         tone="success" icon={<IcCheck />} />
          <ClSoftStat label="Avg first reply" value="< 4h"        sub="typical first reply" tone="accent" icon={<IcActivity />} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 14, alignItems: 'stretch', minHeight: 520 }}>
          {/* List */}
          <div className="card card-pad-sm" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="filter-bar" style={{ border: 'none', padding: '4px 6px 10px' }}>
              <span style={{ color: 'var(--text-muted)', display: 'inline-flex' }}><IcSearch /></span>
              <input className="input" placeholder="Search your requests…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
              {isLoading ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>Loading…</div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>No requests found</div>
              ) : (
                filtered.map(r => (
                  <ClRequestListItem
                    key={r.id}
                    r={r}
                    cmsName={cmsName}
                    active={selected && String(selected.id) === String(r.id)}
                    onClick={() => setSelectedId(String(r.id))}
                  />
                ))
              )}
            </div>
          </div>

          {/* Conversation */}
          <ThreadPanel request={selected} />
        </div>
      </div>
    </>
  )
}

// ── Router wrapper ────────────────────────────────────────────────────────────
const ClientRequests = () => (
  <Routes>
    <Route index element={<ClientRequestsList />} />
    <Route path="new" element={<ClientRequestsList openNew />} />
  </Routes>
)

export default ClientRequests
